<div class="mb-10 max-w-3xl mx-auto px-4 sm:px-6 mt-4">
  <div class="mb-3">
    <app-btn-back routeTo="/groups"></app-btn-back>
  </div>

  <div class="mb-6">
    <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Create an organization</h1>
    <p class="text-xs text-blueGray-500 mt-1">
      Organizations (denominations, networks, dioceses…) group churches into a hierarchy. Tell us about yours and
      attach proof that it's registered. An AtChurch admin reviews every request; once approved, you'll be its
      administrator and can add units, churches and members.
    </p>
  </div>

  @let myRequests = (myRequests$ | async) || [];

  <!-- The user's earlier requests -->
  @if (myRequests.length > 0) {
  <div class="mb-6 space-y-2">
    <h2 class="text-xs font-bold uppercase tracking-wider text-blueGray-400">Your requests</h2>
    @for (r of myRequests; track r.id) {
    <div class="bg-white rounded-xl border border-blueGray-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div class="min-w-0">
        <span class="text-sm font-bold text-blueGray-800 block truncate">{{ r.name }}</span>
        <span class="text-[11px] text-blueGray-500">
          {{ typeLabel(r.orgType) }} · sent {{ r.created?.toDate() | date: 'mediumDate' }}
        </span>
        @if (r.status === 'rejected' && r.reviewNote) {
        <p class="text-[11px] text-rose-700 mt-1">Reason: {{ r.reviewNote }}</p>
        }
      </div>
      @switch (r.status) {
      @case ('pending') {
      <span class="px-2 py-1 rounded-lg text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-100 shrink-0 self-start sm:self-auto">
        <i class="fas fa-hourglass-half mr-1"></i>Waiting for review
      </span>
      }
      @case ('approved') {
      <a [routerLink]="['/management', r.organizationId]"
        class="px-3 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold shrink-0 self-start sm:self-auto">
        Approved · Open
      </a>
      }
      @case ('rejected') {
      <span class="px-2 py-1 rounded-lg text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100 shrink-0 self-start sm:self-auto">
        Not approved
      </span>
      }
      }
    </div>
    }
  </div>
  }

  @if (submittedName) {
  <div class="mb-6 p-4 rounded-xl border border-emerald-200 bg-emerald-50 text-xs text-emerald-800">
    <i class="fas fa-check-circle mr-1.5"></i>
    Your request for <strong>{{ submittedName }}</strong> was sent. We'll email you once it has been reviewed.
  </div>
  }

  <form [formGroup]="form" (ngSubmit)="submit()" class="bg-white rounded-2xl border border-blueGray-200 shadow-2xs p-5 space-y-4">
    <div>
      <label for="orgName" class="block text-xs font-bold text-blueGray-700 mb-1">Organization name *</label>
      <input id="orgName" type="text" formControlName="name" maxlength="120"
        class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
      @if (form.controls.name.touched && form.controls.name.invalid) {
      <p class="text-[11px] text-rose-600 mt-1">Enter the organization's name.</p>
      }
    </div>

    <div>
      <label for="orgPurpose" class="block text-xs font-bold text-blueGray-700 mb-1">Purpose *</label>
      <textarea id="orgPurpose" formControlName="purpose" rows="3" maxlength="2000"
        placeholder="What the organization does and which churches it oversees"
        class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"></textarea>
      @if (form.controls.purpose.touched && form.controls.purpose.invalid) {
      <p class="text-[11px] text-rose-600 mt-1">Describe the organization's purpose.</p>
      }
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label for="orgType" class="block text-xs font-bold text-blueGray-700 mb-1">Registered as *</label>
        <select id="orgType" formControlName="orgType"
          class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500">
          <option value="" disabled>Choose…</option>
          @for (t of types; track t.value) {
          <option [value]="t.value">{{ t.label }}</option>
          }
        </select>
        @if (form.controls.orgType.touched && form.controls.orgType.invalid) {
        <p class="text-[11px] text-rose-600 mt-1">Choose how the organization is registered.</p>
        }
      </div>
      <div>
        <label for="orgReg" class="block text-xs font-bold text-blueGray-700 mb-1">Registration / licence number *</label>
        <input id="orgReg" type="text" formControlName="registrationNumber" maxlength="100"
          class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
        @if (form.controls.registrationNumber.touched && form.controls.registrationNumber.invalid) {
        <p class="text-[11px] text-rose-600 mt-1">Enter the registration or licence number.</p>
        }
      </div>
      <div>
        <label for="orgCountry" class="block text-xs font-bold text-blueGray-700 mb-1">Country of registration *</label>
        <input id="orgCountry" type="text" formControlName="country" maxlength="80"
          class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
        @if (form.controls.country.touched && form.controls.country.invalid) {
        <p class="text-[11px] text-rose-600 mt-1">Enter the country.</p>
        }
      </div>
      <div>
        <label for="orgEmail" class="block text-xs font-bold text-blueGray-700 mb-1">Contact email *</label>
        <input id="orgEmail" type="email" formControlName="contactEmail" maxlength="200"
          class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
        @if (form.controls.contactEmail.touched && form.controls.contactEmail.invalid) {
        <p class="text-[11px] text-rose-600 mt-1">Enter a valid email.</p>
        }
      </div>
      <div>
        <label for="orgPhone" class="block text-xs font-bold text-blueGray-700 mb-1">Contact phone</label>
        <input id="orgPhone" type="tel" formControlName="contactPhone" maxlength="40"
          class="w-full px-3 py-2 text-sm rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
      </div>
    </div>

    <!-- Supporting documents -->
    <div>
      <span class="block text-xs font-bold text-blueGray-700 mb-1">Proof of registration *</span>
      <p class="text-[11px] text-blueGray-500 mb-2">
        Certificate of registration, charity or business licence, or similar. PDF or image, up to
        {{ maxDocumentMb }} MB each, {{ maxDocuments }} files at most. Only you and AtChurch admins can see them.
      </p>
      @for (f of files; track f.name + f.size; let i = $index) {
      <div class="flex items-center justify-between gap-2 px-3 py-2 mb-1.5 rounded-lg border border-blueGray-200 bg-blueGray-50/60">
        <span class="text-xs text-blueGray-700 truncate">
          <i class="fas mr-1.5 text-blueGray-400" [class.fa-file-pdf]="f.type === 'application/pdf'"
            [class.fa-file-image]="f.type !== 'application/pdf'"></i>{{ f.name }}
          <span class="text-blueGray-400">({{ fileSize(f.size) }})</span>
        </span>
        <button type="button" (click)="removeFile(i)" [disabled]="submitting" title="Remove"
          class="h-6 w-6 rounded hover:bg-rose-50 text-blueGray-400 hover:text-rose-600 cursor-pointer shrink-0">
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
      }
      @if (files.length < maxDocuments) {
      <label
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-blueGray-300 hover:border-primary-400 text-xs font-semibold text-blueGray-600 hover:text-primary-600 cursor-pointer">
        <i class="fas fa-paperclip text-[11px]"></i>Attach documents
        <input type="file" multiple accept="application/pdf,image/png,image/jpeg" class="hidden"
          (change)="onFilesPicked($event)" [disabled]="submitting" />
      </label>
      }
      @if (fileError) {
      <p class="text-[11px] text-rose-600 mt-1">{{ fileError }}</p>
      }
    </div>

    <div class="flex justify-end pt-2">
      <button type="submit" [disabled]="submitting"
        class="px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-semibold shadow-2xs cursor-pointer">
        @if (submitting) {
        <i class="fas fa-circle-notch fa-spin mr-1.5"></i>Sending…
        } @else {
        Send request for review
        }
      </button>
    </div>
  </form>
</div>
